import React, { Component, PropTypes } from 'react';
import moment from 'moment-timezone';
import partial from 'lodash/partial';
import orderBy from 'lodash/orderBy';
import classnames from 'classnames';
import { LoadingIndicator } from 'common/components/preloader';
import Message from 'common/components/message';
import MessageEditor from 'common/containers/message_editor';
import Avatar from 'common/components/avatar';
import CsAvatar from 'assets/images/crowdspring_avatar.png';

const ALWAYS_VISIBLE_THREAD_COUNT = 5;

/**
 * Message stream timeline.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class MessageTimeline extends Component {
  constructor(props) {
    super(props);

    this.state = { replyingTo: undefined, showingAll: false, sorting: "newest" };

    this.toggleEditor = this.toggleEditor.bind(this);
    this.setSorting = this.setSorting.bind(this);
  }

  setSorting(sorting) {
    this.setState({ sorting, showingAll: false });
  }

  toggleEditor(replyingTo) {
    this.setState({ replyingTo: replyingTo });
  }

  renderThreadRow(msg, key) {
    const { user, wrapup, wrapupStep } = this.props;
    const isAuthor = msg.author? msg.author.id === user.id : user.is_staff;

    if (msg.hidden) {
      return null;
    }

    return (
      <div className="row thread-row" key={key}>
        <div className="columns small-16">
          <label className="timestamp">{ moment(msg.created_at).fromNow() }</label>
        </div>
        <div className={"columns small-3 large-2 small-order-" + (isAuthor? "1":"2")}>
          <Avatar user={ msg.author || { avatar: CsAvatar } }
            extraClasses="avatar-md avatar-round" />
        </div>
        <div className={"columns small-13 large-14 small-order-" + (isAuthor? "2":"1")}>
          <Message message={msg} user={user} wrapup={wrapup} wrapupStep={wrapupStep} />
        </div>
      </div>)
  }

  render() {
    const {
      threads, user, fetching, wrapup, canPost, canAttach, canPostInternal, editorLabel,
      editorActionLabel, startAt, emptyLabel, project, editorPrepopulatedOpts, wrapupStep
    } = this.props;
    const { replyingTo, showingAll, sorting } = this.state;

    if (fetching) {
      return <LoadingIndicator fullScreen={false} />;
    }

    const showEditor = canPost && replyingTo === undefined;

    /* if `startAt` is specified, filter out threads that were created before it */
    const validThreads = startAt? threads.filter(thread => {
      /* older messages come first in the thread */
      const oldestMsg = thread[0];
      return !moment(oldestMsg.created_at).isBefore(startAt);
    }) : threads;

    const sortedThreads = orderBy(
      validThreads, t => moment(t[0].created_at), sorting === "newest"? "desc" : "asc"
    );

    return (
      <div className="message-timeline">
        { showEditor &&
        <MessageEditor wrapup={wrapup} canAttach={canAttach} canPostInternal={canPostInternal}
          label={editorLabel} actionLabel={editorActionLabel} project={project}
          prepopulatedOpts={editorPrepopulatedOpts} wrapupStep={wrapupStep} /> }

        <div className={classnames("sorting", { "with-border": showEditor })}>
          { ["newest", "oldest"].map(s => (
          <span key={s} className={classnames("factor", { active: sorting === s })}
            onClick={partial(this.setSorting, s)}>{ s }</span>
          ) )}
        </div>

        { sortedThreads.length === 0 &&
        <div className="empty-state">
          <h3>{ emptyLabel || "There are no messages at this time."}</h3>
        </div> }

        { sortedThreads.map((thread, i) => {
          /* hide threads that are not supposed to be visible */
          if (!showingAll && i >= ALWAYS_VISIBLE_THREAD_COUNT) {
            return null
          }

          /* older messages come first in the thread */
          const newestMsg = thread.slice(-1)[0]

          /* system messages (no author) and internal messages can't be replied to */
          const canReply = (
            this.props.canReply && canPost && !!newestMsg.author && !newestMsg.internal
          );

          return (
            <div className="thread" key={i}>
              { thread.map((msg, j) => this.renderThreadRow(msg, j)) }

              { replyingTo === newestMsg.id &&
              <MessageEditor onCancel={partial(this.toggleEditor, undefined)}
                onDone={partial(this.toggleEditor, undefined)}
                replyTo={replyingTo} wrapup={wrapup} project={project}
                canAttach={canAttach} wrapupStep={wrapupStep} /> }

              { replyingTo !== newestMsg.id && canReply &&
              <div className="row">
                <div className="columns small-13 small-offset-3 large-14 large-offset-2">
                  <button className="btn btn-secondary btn-sm btn-reply"
                          onClick={partial(this.toggleEditor, newestMsg.id)}>
                    Reply
                  </button>
                </div>
              </div> }
            </div>)
        }) }

        { sortedThreads.length > ALWAYS_VISIBLE_THREAD_COUNT &&
        <button className="btn btn-text btn-md btn-more"
                onClick={() => this.setState({showingAll: !showingAll})}>
          { `${showingAll? 'Hide' : 'Show'} ${sorting === 'newest'? 'older' : 'newer'}` }
        </button> }
      </div>
    )
  }
}

MessageTimeline.defaultProps = {
  canReply: true
};

MessageTimeline.propTypes = {
  threads: PropTypes.array,
  user: PropTypes.object,
  fetching: PropTypes.bool,
  canPost: PropTypes.bool,
  canAttach: PropTypes.bool,
  canPostInternal: PropTypes.bool,
  canReply: PropTypes.bool,
  emptyLabel: PropTypes.string,
  editorLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  editorActionLabel: PropTypes.string,
  startAt: PropTypes.string,
  wrapup: PropTypes.object,
  wrapupStep: PropTypes.string,
  project: PropTypes.object,
  editorPrepopulatedOpts: PropTypes.array,
};



// WEBPACK FOOTER //
// ./common/components/message_timeline.jsx